import { useCallback, useEffect, useRef, useState } from "react"
import { Box, Skeleton, Typography, useMediaQuery } from "@mui/material"

const MOBILE_WIDTH = 320
const MOBILE_HEIGHT = 693

// To switch a card from a live iframe to a local video, add
// `video: "/videos/showcase-<name>.mp4"` (and optionally `poster`) to the user below.
const showcaseUsers = [
    {
        username: "Chris",
        role: "Builder",
        quote: "Vibio is AWESOME! I've been waiting for exactly this! So much more aesthetically pleasing.",
        path: "Chris",
        video: "/videos/showcase-chris.mp4",
        poster: "/videos/showcase-chris-poster.jpg",
    },
    {
        username: "Mandamus Lawyers",
        role: "Law Firm",
        quote: "Vibio helps us create a professional yet engaging hub for our law firm, making it easier for potential clients to access our services.",
        path: "Mandamuslawyers",
        video: "/videos/showcase-mandamus.mp4",
        poster: "/videos/showcase-mandamus-poster.jpg",
    },
    {
        username: "Albert",
        role: "Hiker & Runner",
        quote: "Vibio makes my hikes look good and feel genuine. I love how I can track my affiliate link performance with Revenue Link stats.",
        path: "albert-hikes",
        video: "/videos/showcase-albert.mp4",
        poster: "/videos/showcase-albert-poster.jpg",
    },
]

function ShowcaseCard({ user }) {
    const containerRef = useRef(null)
    const [scale, setScale] = useState(1)
    const [loading, setLoading] = useState(true)

    const updateScale = useCallback(() => {
        if (containerRef.current) {
            setScale(containerRef.current.offsetWidth / MOBILE_WIDTH)
        }
    }, [])

    useEffect(() => {
        updateScale()
        window.addEventListener('resize', updateScale)
        return () => window.removeEventListener('resize', updateScale)
    }, [updateScale])

    const scaledHeight = MOBILE_HEIGHT * scale

    return (
        <a
            href={`https://vibio.co/${user.path}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', display: 'block' }}
        >
            <Box
                ref={containerRef}
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: scaledHeight,
                    borderRadius: '24px',
                    overflow: 'hidden',
                    border: '1.5px solid #e8e8e8',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    backgroundColor: '#111',
                }}
            >
                {loading && (
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        animation="wave"
                        sx={{ position: 'absolute', zIndex: 1, bgcolor: 'grey.800' }}
                    />
                )}
                {user.video ? (
                    <video
                        muted
                        playsInline
                        autoPlay
                        loop
                        preload="metadata"
                        src={user.video}
                        poster={user.poster}
                        title={user.username}
                        onLoadedData={() => setLoading(false)}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                            pointerEvents: 'none',
                        }}
                    />
                ) : (
                    <div style={{
                        transform: `scale(${scale})`,
                        transformOrigin: 'top left',
                    }}>
                        <iframe
                            src={`https://vibio.co/${user.path}?preview=true&mobile=true`}
                            title={user.username}
                            frameBorder="0"
                            allow="autoplay"
                            scrolling="no"
                            onLoad={() => setLoading(false)}
                            style={{
                                width: MOBILE_WIDTH,
                                height: MOBILE_HEIGHT,
                                border: 'none',
                                pointerEvents: 'none',
                            }}
                        />
                    </div>
                )}
            </Box>
        </a>
    )
}

function UserInfo({ user }) {
    return (
        <>
            <Typography sx={{
                fontFamily: 'Catamaran',
                fontWeight: 700,
                fontSize: '1rem',
                color: 'primary.black',
                mt: 1.5,
            }}>
                {user.username}
            </Typography>
            {/* <Typography sx={{
                fontFamily: 'Catamaran',
                fontWeight: 500,
                fontSize: '0.85rem',
                color: 'text.gray',
                mb: 0.5,
            }}>
                {user.role}
            </Typography> */}
            <Typography sx={{
                fontFamily: 'Catamaran',
                fontWeight: 400,
                fontSize: '0.85rem',
                color: 'text.gray',
                fontStyle: 'italic',
                mt: 0.5,
            }}>
                "{user.quote}"
            </Typography>
        </>
    )
}

export default function ShowcaseSection() {
    const isMobile = useMediaQuery('(max-width:950px)')
    const sectionRef = useRef(null)
    const [isVisible, setIsVisible] = useState(false)
    const [activeIndex, setActiveIndex] = useState(0)
    const [fading, setFading] = useState(false)

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true)
                    observer.disconnect()
                }
            },
            { rootMargin: '200px' }
        )
        if (sectionRef.current) observer.observe(sectionRef.current)
        return () => observer.disconnect()
    }, [])

    // Auto-rotate on mobile
    useEffect(() => {
        if (!isMobile) return
        const interval = setInterval(() => {
            setFading(true)
            setTimeout(() => {
                setActiveIndex(prev => (prev + 1) % showcaseUsers.length)
                setFading(false)
            }, 300)
        }, 4000)
        return () => clearInterval(interval)
    }, [isMobile])

    const goToSlide = (index) => {
        setFading(true)
        setTimeout(() => {
            setActiveIndex(index)
            setFading(false)
        }, 300)
    }

    return (
        <Box ref={sectionRef} sx={{
            margin: isMobile ? '1.2rem .8rem' : '4rem 14vw',
            padding: isMobile ? '2rem 1.2rem' : '3rem 6vw',
            fontFamily: 'Catamaran',
        }}>
            <Typography
                variant={isMobile ? 'h4' : 'h3'}
                component="h2"
                sx={{
                    fontFamily: 'inherit',
                    fontWeight: 900,
                    color: 'primary.black',
                    textAlign: 'center',
                    mb: 4,
                    fontSize: isMobile ? '1.8rem' : '2rem',
                }}
            >
                See Vibio in Action
            </Typography>

            {isMobile ? (
                /* Mobile: single card with auto-rotate */
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{
                        width: '70%',
                        maxWidth: 280,
                        opacity: fading ? 0 : 1,
                        transition: 'opacity 0.3s ease',
                    }}>
                        {isVisible && <ShowcaseCard key={showcaseUsers[activeIndex].path} user={showcaseUsers[activeIndex]} />}
                        <UserInfo user={showcaseUsers[activeIndex]} />
                    </Box>

                    {/* Dot indicators */}
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 3, justifyContent: 'center' }}>
                        {showcaseUsers.map((_, i) => (
                            <Box
                                key={i}
                                onClick={() => goToSlide(i)}
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: i === activeIndex ? 'primary.main' : '#ccc',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.3s',
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            ) : (
                /* Desktop: 3 cards centered in a row */
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 3,
                }}>
                    {showcaseUsers.map((user) => (
                        <Box key={user.username} sx={{
                            flex: '0 1 280px',
                            maxWidth: 280,
                        }}>
                            {isVisible && <ShowcaseCard user={user} />}
                            <UserInfo user={user} />
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    )
}
